'use client';
import type { SanityPromo } from '@metmuseum/types';
import type { IEvent } from 'lib/search/eventSearch';
import { newYorkizeDate } from 'helpers/date-time';
import { PromoCard } from 'components/promos/promo-card';
import styles from 'components/promos/promos.module.scss';
import EventPromoCard from 'components/promos/event-promo-card';
import { getContextAttributes } from 'lib/analytics/analyticsAttributes';

const isEvent = (
	possiblyAnEvent: IEvent | SanityPromo,
): possiblyAnEvent is IEvent => {
	return !!possiblyAnEvent.aceId; // Silly promo, aceIds are for Events!
};

const getValidEvents = (events: IEvent[]): IEvent[] => {
	const now = new Date(newYorkizeDate(new Date().toISOString()).toISOString());
	const validEvents =
		events?.filter((event) => new Date(event.startDate) > now) || [];
	const eventMap = new Map();
	// Multiple performances will have the same url, we only need one instance
	// Reverse the array so the earlier events overwrite the later ones in the map
	for (const event of validEvents.reverse()) {
		eventMap.set(event.url, event);
	}

	return [...eventMap.values()]
		.sort(
			(a, b) =>
				new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
		)
		.slice(0, 3);
};

/*
 * This component is strictly for the Events Landing Page. It is a client component
 * because it needs to be able to hide events dynamically based on current time/date.
 * If you only have Promo items, use <Promos /> instead.
 */
const EventsPromoHero = ({ featuredEvents, promos }: IParams) => {
	let events: (IEvent | SanityPromo)[] = getValidEvents(featuredEvents);
	if (events.length < 3) {
		events = [...events, ...promos.slice(0, 3 - events.length)];
	}

	return (
		<section
			className={styles.promoContainer}
			{...getContextAttributes('events-featured-card')}
		>
			{events.map((event, index) => {
				if (isEvent(event)) {
					return (
						<EventPromoCard
							key={`promo${event.aceId}${event.startDate}`}
							event={event}
							isReversed={index % 2 != 0}
						/>
					);
				} else {
					const promo = event; // otherwise, it feels wrong
					return (
						<PromoCard
							key={promo._id}
							promo={promo}
							isReversed={index % 2 != 0}
						/>
					);
				}
			})}
		</section>
	);
};

interface IParams {
	featuredEvents: IEvent[];
	promos: SanityPromo[];
}

export default EventsPromoHero;
